import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'
import type {
  Product,
  ProgressiveDiscount,
} from 'src/components/restructure/product/types/product'
import type { Variation } from 'src/components/product-page/Selector/types'
import { useAddressContext } from 'src/components/checkout/steps/shipping/context/address-context'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { isPreSale as isPreSaleFn } from 'src/utils/pdp/isPreSale'
import { PAGE_TYPE_MAP } from 'src/hooks/usePageType'
import { HAVE_WINDOW } from 'src/constants'
import type { KitlookData } from 'src/types/kitlooks'

import { useFreightSimulation } from '../../common/FreightSimulation/useFreightSimulation'
import type {
  Item,
  FreightSimulationData,
  FreightPickupPoint,
} from '../../common/FreightSimulation/useFreightSimulation'
import { getInitialItem } from '../../utils/get-initial-sku'

type ProductProviderProps = {
  children: React.ReactNode
  product: Product
  slug: string
  skuId?: string | null
  progressiveDiscount?: ProgressiveDiscount
  kitlooks?: KitlookData[] | null
}

type ProductContextProps = {
  product: Product
  isPreSale: boolean
  quantity: number
  setQuantity: Dispatch<SetStateAction<number>>
  currentSku: ProductItem
  initialSku: ProductItem
  kitlooks: KitlookData[]
  setCurrentSku: Dispatch<SetStateAction<ProductItem>>
  deliveryIsAvailable: boolean
  setDeliveryIsAvailable: Dispatch<SetStateAction<boolean>>
  setFreightSimulation: (items: Item[], cep: string) => Promise<void>
  isLoadingFreightSimulation: boolean
  freightSimulationValue: FreightSimulationData | null
  variations: Variation[]
  setVariations: Dispatch<SetStateAction<Variation[]>>
  selectedPickupPoint: FreightPickupPoint | null
  setSelectedPickupPoint: Dispatch<SetStateAction<FreightPickupPoint | null>>
  setFreightSimulationValue: Dispatch<
    SetStateAction<FreightSimulationData | null>
  >
  simulationError: boolean
  setSimulationError: Dispatch<SetStateAction<boolean>>
  progressiveDiscount?: ProgressiveDiscount
}

const EMPTY_KITLOOKS: KitlookData[] = []

const useIsomorphicLayoutEffect = HAVE_WINDOW ? useLayoutEffect : useEffect

export const ProductContext = createContext<ProductContextProps>(
  {} as ProductContextProps
)

type ProductStore = {
  getSnapshot: () => ProductContextProps
  subscribe: (onStoreChange: () => void) => () => void
}

/**
 * Backs `useProductContextSelector`. Kept separate from `ProductContext` so
 * granular subscribers (via useSyncExternalStore) don't force a re-render of
 * every `useContext(ProductContext)` consumer on every store notification.
 */
const ProductStoreContext = createContext<ProductStore | null>(null)

export const ProductProvider = ({
  children,
  product,
  progressiveDiscount,
  slug,
  skuId = null,
  kitlooks = EMPTY_KITLOOKS,
}: ProductProviderProps) => {
  const initialSku = getInitialItem(product.items, slug, skuId)
  const { getFreightSimulation } = useFreightSimulation()
  const { postalCode } = useAddressContext()
  const { pageData } = useGlobalContext()

  const [currentSku, setCurrentSku] = useState(initialSku)
  const isPreSale = currentSku
    ? isPreSaleFn(currentSku.estimatedDateArrival)
    : false

  const [quantity, setQuantity] = useState(1)

  const [deliveryIsAvailable, setDeliveryIsAvailable] = useState(false)

  const [isLoadingFreightSimulation, setIsLoadingFreightSimulation] =
    useState(false)

  const [simulationError, setSimulationError] = useState<boolean>(false)

  const [freightSimulationValue, setFreightSimulationValue] =
    useState<FreightSimulationData | null>(null)

  const [variations, setVariations] = useState<Variation[]>([])
  const [selectedPickupPoint, setSelectedPickupPoint] =
    useState<FreightPickupPoint | null>(null)

  useEffect(() => {
    // to change current sku when product is changed
    setCurrentSku(getInitialItem(product.items, slug, skuId))
  }, [product, skuId, slug])

  useEffect(() => {
    setQuantity(1)
  }, [currentSku])

  const sendDYEvent = () => {
    if (!HAVE_WINDOW) return

    window?.DY?.API('event', {
      name: 'shipping-simulation-exposure',
    })
  }

  const setFreightSimulation = useCallback(
    async (items: Item[], cep: string) => {
      setIsLoadingFreightSimulation(true)

      try {
        const response = await getFreightSimulation(items, cep)

        const isDeliveryAvailable = response?.packages?.some(
          (item) => item?.deliveryTypes?.length > 0
        )

        setDeliveryIsAvailable(isDeliveryAvailable ?? false)
        setFreightSimulationValue(response ?? null)
        setSelectedPickupPoint(
          response?.packages?.[0]?.pickupPoints?.recommend?.[0] ?? null
        )
        setSimulationError(!response)
        sendDYEvent()
      } catch (error) {
        console.error('Error during freight simulation:', error)
        setSimulationError(true)
      } finally {
        setIsLoadingFreightSimulation(false)
      }
    },
    [
      getFreightSimulation,
      setIsLoadingFreightSimulation,
      setDeliveryIsAvailable,
      setFreightSimulationValue,
      setSelectedPickupPoint,
    ]
  )

  useEffect(() => {
    async function simulateWhenChangeSKU() {
      if (!postalCode || !currentSku) {
        return
      }

      setFreightSimulation(
        [
          {
            id: currentSku?.itemId ?? '',
            seller: currentSku?.sellerDefault?.sellerId ?? '',
            quantity,
          },
        ],
        postalCode
      )
    }

    simulateWhenChangeSKU()
  }, [postalCode, currentSku, setFreightSimulation, quantity])

  useEffect(() => {
    const productObject = getProductQueryObject(currentSku)

    const standardObject = getStandardObject(pageData)

    if (
      !standardObject ||
      !productObject ||
      pageData.currentPageType !== PAGE_TYPE_MAP.pdp
    ) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Product Viewed',
      eventData: {
        ...standardObject,
        'product details': [productObject],
        'product is available': currentSku?.isAvailable,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSku, pageData?.currentPageType])

  const productContextValue = useMemo(
    () => ({
      product,
      kitlooks: kitlooks ?? EMPTY_KITLOOKS,
      isPreSale,
      initialSku,
      quantity,
      setQuantity,
      currentSku,
      setCurrentSku,
      deliveryIsAvailable,
      setDeliveryIsAvailable,
      setFreightSimulation,
      isLoadingFreightSimulation,
      freightSimulationValue,
      variations,
      setVariations,
      selectedPickupPoint,
      setSelectedPickupPoint,
      setFreightSimulationValue,
      simulationError,
      setSimulationError,
      progressiveDiscount,
    }),
    [
      product,
      kitlooks,
      isPreSale,
      initialSku,
      quantity,
      setQuantity,
      currentSku,
      setCurrentSku,
      deliveryIsAvailable,
      setDeliveryIsAvailable,
      setFreightSimulation,
      isLoadingFreightSimulation,
      freightSimulationValue,
      variations,
      setVariations,
      selectedPickupPoint,
      setSelectedPickupPoint,
      setFreightSimulationValue,
      simulationError,
      setSimulationError,
      progressiveDiscount,
    ]
  )

  const storeRef = useRef<{
    value: ProductContextProps
    listeners: Set<() => void>
  }>()

  if (!storeRef.current) {
    storeRef.current = { value: productContextValue, listeners: new Set() }
  }

  useIsomorphicLayoutEffect(() => {
    storeRef.current!.value = productContextValue
    storeRef.current!.listeners.forEach((listener) => listener())
  }, [productContextValue])

  const store = useMemo<ProductStore>(
    () => ({
      getSnapshot: () => storeRef.current!.value,
      subscribe: (onStoreChange) => {
        storeRef.current!.listeners.add(onStoreChange)

        return () => storeRef.current!.listeners.delete(onStoreChange)
      },
    }),
    []
  )

  return (
    <ProductStoreContext.Provider value={store}>
      <ProductContext.Provider value={productContextValue}>
        {children}
      </ProductContext.Provider>
    </ProductStoreContext.Provider>
  )
}

export const useProductPageContext = () => useContext(ProductContext)

export function useProductContextSelector<T>(
  selector: (ctx: ProductContextProps) => T
): T {
  const store = useContext(ProductStoreContext)

  if (!store) {
    throw new Error(
      'useProductContextSelector must be used within a ProductProvider'
    )
  }

  const getSnapshot = () => selector(store.getSnapshot())

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot)
}

const noopSubscribe = () => () => {}

/**
 * Non-throwing variant for hooks shared with components rendered outside a
 * ProductProvider (product cards on search, PLP and shelves reuse
 * `useBuyButton`). Returns `undefined` when there is no provider.
 */
export function useOptionalProductContextSelector<T>(
  selector: (ctx: ProductContextProps) => T
): T | undefined {
  const store = useContext(ProductStoreContext)

  const getSnapshot = () => (store ? selector(store.getSnapshot()) : undefined)

  return useSyncExternalStore(
    store ? store.subscribe : noopSubscribe,
    getSnapshot,
    getSnapshot
  )
}

/**
 * Same typing as `useProductContextSelector`, but without throwing outside a
 * ProductProvider: the selector result is `undefined` at runtime. It keeps the
 * semantics of the former `useContext(ProductContext)` (default `{}`) for
 * components that receive their data via props when rendered outside the PDP
 * (card SKU modal, cross-sell, kits) and only fall back to the context.
 */
export function useLooseProductContextSelector<T>(
  selector: (ctx: ProductContextProps) => T
): T {
  return useOptionalProductContextSelector(selector) as T
}
